import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';

const isDev = process.env.NODE_ENV === 'development';

interface Props {
  show: boolean;
  onHide: () => void;
  onSwitch: (username: string) => void;
  currentUsername?: string;
  allowedUsernames?: string[];
  isAdmin?: boolean;
  switchedFromUsername?: string;
}

const SwitchUserDropdown: React.FC<Props> = ({ show, onHide, onSwitch, currentUsername, allowedUsernames, isAdmin, switchedFromUsername }) => {
  const [usernames, setUsernames] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;

    const normalizedAllowedUsernames = (allowedUsernames || []).filter(Boolean);

    if (!isAdmin && !switchedFromUsername && normalizedAllowedUsernames.length) {
      const filtered = normalizedAllowedUsernames.filter(u => u.toLowerCase() !== (currentUsername || '').toLowerCase());
      setUsernames(filtered);
      setSelected(filtered[0] || '');
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    fetch((isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com') + '/user/list-usernames', {
      headers: { Authorization: localStorage.getItem('authToken') || '' }
    })
      .then(res => res.ok ? res.json() : Promise.reject(res.statusText))
      .then(data => {
        const current = (currentUsername || '').toLowerCase();
        const filtered = data.filter((u: string) => u.toLowerCase() !== current);
        setUsernames(filtered);
        setSelected(filtered[0] || '');
        setLoading(false);
      })
      .catch(err => { setError('Failed to load users'); setLoading(false); });
  }, [show, currentUsername, allowedUsernames, isAdmin, switchedFromUsername]);

  const handleSwitch = () => {
    if (selected) onSwitch(selected);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Switch User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? <Spinner animation="border" /> : error ? <div>{error}</div> : (
          <Form>
            <Form.Group controlId="switchUserSelect">
              <Form.Label>Select user</Form.Label>
              <Form.Select value={selected} onChange={e => setSelected(e.target.value)}>
                {usernames.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        <Button variant="primary" onClick={handleSwitch} disabled={!selected || loading}>Switch</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SwitchUserDropdown;
